<template>
  <div v-if="mode === 'import'" class="vtt-upload-section mt-3">
    <div class="card">
      <div class="card-header bg-light d-flex justify-content-between align-items-center">
        <h6 class="mb-0 font-weight-bold">
          <i class="fa fa-upload mr-2"></i>Closed Caption import
        </h6>
        <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            @click="emit('toggle-section')"
        >
          <i :class="['fa', importOpts.sectionVisible ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
        </button>
      </div>
      <div v-show="importOpts.sectionVisible" class="card-body">
        <p class="text-muted small mb-3">
          Upload a WebVTT (.vtt) file to automatically generate timeline events from subtitle cues.
          Each cue will be imported as a text event with the corresponding timestamps.
        </p>
        <div class="custom-file mb-3">
          <input
              type="file"
              class="custom-file-input"
              :id="importOpts.fileInputId"
              accept=".vtt"
              ref="fileInput"
              :disabled="events.length > 0"
              @change="onFileChange"
          >
          <label class="custom-file-label" :for="importOpts.fileInputId" :class="{ 'disabled': events.length > 0 }">
            {{ importOpts.fileName || 'Choose VTT file...' }}
          </label>
        </div>
        <div v-if="events.length > 0" class="vtt-preview">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0 font-weight-bold">VTT Cues ({{ events.length }} found)</h6>
          </div>

          <timeline-events-table
              :events="events"
              :show-drag-handle="true"
              :is-vtt-mode="true"
              :empty-message="emptyMessage"
              @update:events="emit('update:events', $event)"
              @set-media-type="() => {}"
              @format-time="(index, field) => emit('format-time', index, field)"
              @until-end-change="() => {}"
              @update-field="(index, field, value) => emit('update-field', index, field, value)"
              @remove="(index) => emit('remove', index)"
          />

          <div class="mt-3 actions-section">
            <div class="d-flex justify-content-between align-items-center">
              <button
                  type="button"
                  class="btn btn-primary btn-sm"
                  :disabled="addCueOpts.disabled"
                  :title="addCueOpts.disabled ? addCueOpts.title : ''"
                  @click="onAddClick"
              >
                <i class="fa fa-plus"></i> Add Cue
              </button>
              <button
                  type="button"
                  class="btn btn-secondary btn-sm"
                  :title="clearButtonTitle"
                  @click="onClearClick"
              >
                <i class="fa fa-times"></i> Clear
              </button>
            </div>
            <small v-if="addCueOpts.disabled" class="text-danger d-block mt-2">
              {{ addCueOpts.hint }}
            </small>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-else-if="mode === 'per-event'" class="closed-captions-per-event mt-2">
    <div class="d-flex align-items-center justify-content-between">
      <label class="small text-muted mb-0">
        <i class="fa fa-closed-captioning mr-1"></i>
        Closed Captions
      </label>
      <div>
        <button
            type="button"
            class="btn btn-sm btn-outline-primary"
            @click="emit('upload-vtt')"
            :disabled="perEventOpts.uploadDisabled"
        >
          <i class="fa fa-upload mr-1"></i>
          {{ uploadButtonLabel }}
        </button>
        <button
            v-if="hasCaptions"
            type="button"
            :class="['btn', 'btn-sm', perEventOpts.expanded ? 'btn-secondary' : 'btn-outline-secondary', 'ml-1']"
            @click="emit('toggle-captions')"
            :title="perEventOpts.expanded ? 'Hide captions' : 'Show captions'"
        >
          <i :class="['fa', perEventOpts.expanded ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import TimelineEventsTable from './TimelineEventsTable.vue'

const fileInput = ref(null)
const props = defineProps({
  mode: {
    type: String,
    required: true,
    validator: (v) => ['import', 'per-event'].includes(v)
  },
  events: {
    type: Array,
    default: () => []
  },
  importOptions: {
    type: Object,
    default: () => ({})
  },
  perEventOptions: {
    type: Object,
    default: () => ({})
  },
  addCueOptions: {
    type: Object,
    default: () => ({})
  },
  emptyMessage: {
    type: String,
    default: 'No captions added yet.'
  }
})

const importOpts = computed(() => ({
  sectionVisible: true,
  fileName: '',
  fileInputId: 'vttFileInput',
  ...props.importOptions
}))

const perEventOpts = computed(() => ({
  expanded: false,
  uploadDisabled: false,
  tableBelowRow: false,
  ...props.perEventOptions
}))

const addCueOpts = computed(() => ({
  disabled: false,
  title: 'Please complete the current cue (Text, Start Time, End Time) before adding a new one',
  hint: 'Complete the current cue (Text, Start Time, End Time) before adding a new one',
  ...props.addCueOptions
}))

const emit = defineEmits([
  'update:events',
  'toggle-section',
  'file-selected',
  'format-time',
  'update-field',
  'remove',
  'add-cue',
  'clear',
  'upload-vtt',
  'toggle-captions',
  'clear-vtt',
  'add-caption',
  'vtt-uploaded'
])

const hasCaptions = computed(() => props.events && props.events.length > 0)

const uploadButtonLabel = computed(() =>
  hasCaptions.value ? `${props.events.length} cues` : 'Upload VTT'
)

const clearButtonTitle = computed(() =>
  props.mode === 'import' ? 'Clear all closed captions' : 'Clear captions'
)

function onAddClick() {
  emit(props.mode === 'import' ? 'add-cue' : 'add-caption')
}

function onClearClick() {
  emit(props.mode === 'import' ? 'clear' : 'clear-vtt')
}

function onFileChange(event) {
  const file = event.target && event.target.files[0]
  if (file) {
    emit('file-selected', file)
  }
}

watch(() => props.events.length, (len) => {
  if (len === 0 && fileInput.value) {
    fileInput.value.value = ''
  }
})
</script>

<style lang="scss" scoped>
.vtt-upload-section {
  .card {
    border: 1px solid #dee2e6;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

    .card-header {
      padding: 0.75rem 1rem;
      border-bottom: 1px solid #dee2e6;

      h6 {
        color: #495057;
        font-size: 0.95rem;

        i {
          color: #007bff;
        }
      }

      .btn {
        padding: 0.25rem 0.5rem;
        font-size: 0.875rem;
      }
    }

    .card-body {
      padding: 1rem;
    }
  }

  .custom-file-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &.disabled {
      background-color: #e9ecef;
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .vtt-preview {
    margin-top: 1rem;

    .table-responsive {
      max-height: 500px;
      overflow-y: auto;
    }
  }
}

.closed-captions-per-event {
  .captions-section {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 0.75rem;
  }
}
</style>
